import type { MasteryMapDomain } from '@ts-mastery-map/interfaces'
import type {
  MasteryHighlights,
  MasterySkill,
  MasteryStatusCounts,
  MasteryStrand,
  MasteryTestColumn,
  MasteryTestSnapshot,
} from '@app/types/mastery-map-dashboard/interfaces'
import type { MasterySkillStatus } from '@app/types/mastery-map-dashboard/types'

import { getStrandTitle } from '@utils/mastery-map/strand-titles'

const FULL_PERCENT = 100

const countStatus = (skills: MasterySkill[], status: MasterySkillStatus): number =>
  skills.filter(skill => skill.status === status).length

export const toPercentage = (part: number, total: number): number =>
  total > 0 ? Math.round((part / total) * FULL_PERCENT) : 0

export const getStatusCounts = (snapshot: MasteryTestSnapshot): MasteryStatusCounts => ({
  mastered: countStatus(snapshot.skills, 'mastered'),
  developing: countStatus(snapshot.skills, 'developing'),
  upNext: snapshot.upNextCount,
})

const getStrandOrder = (code: string, domains: MasteryMapDomain[]): number => {
  const index = domains.findIndex(domain => domain.code === code)

  return index === -1 ? domains.length : index
}

export const getStrandCodes = (snapshot: MasteryTestSnapshot, domains: MasteryMapDomain[]): string[] =>
  [...new Set(snapshot.skills.map(skill => skill.strand))]
    .sort((a, b) => getStrandOrder(a, domains) - getStrandOrder(b, domains) || a.localeCompare(b))

export const getStrand = (
  snapshot: MasteryTestSnapshot,
  code: string,
  domains: MasteryMapDomain[],
): MasteryStrand => {
  const skills = snapshot.skills.filter(skill => skill.strand === code)
  const counts: MasteryStatusCounts = {
    mastered: countStatus(skills, 'mastered'),
    developing: countStatus(skills, 'developing'),
    upNext: countStatus(skills, 'up-next'),
  }

  return {
    code,
    title: getStrandTitle(code, domains),
    total: skills.length,
    counts,
    percentage: toPercentage(counts.mastered, skills.length),
  }
}

export const getStrands = (snapshot: MasteryTestSnapshot, domains: MasteryMapDomain[]): MasteryStrand[] =>
  getStrandCodes(snapshot, domains).map(code => getStrand(snapshot, code, domains))

export const getMasteredGain = (snapshots: MasteryTestSnapshot[]): number | null => {
  const first = snapshots.at(0)
  const latest = snapshots.at(-1)

  if (!first || !latest || first === latest) return null

  return getStatusCounts(latest).mastered - getStatusCounts(first).mastered
}

export const getTestColumns = (snapshots: MasteryTestSnapshot[]): MasteryTestColumn[] => {
  const first = snapshots.at(0)
  const firstMastered = first ? getStatusCounts(first).mastered : 0

  return snapshots.map((snapshot, index) => {
    const mastered = getStatusCounts(snapshot).mastered

    return {
      test: snapshot.test,
      mastered,
      gain: index === 0 ? null : mastered - firstMastered,
    }
  })
}

const isMasteredAt = (snapshot: MasteryTestSnapshot, code: string): boolean =>
  snapshot.skills.some(skill => skill.code === code && skill.status === 'mastered')

/** Average number of tests each skill of the strand took to reach Mastered; unmastered skills count as the slowest. */
const getMasteryPace = (snapshots: MasteryTestSnapshot[], strand: string): number => {
  const latest = snapshots.at(-1)
  const codes = latest?.skills.filter(skill => skill.strand === strand).map(skill => skill.code) ?? []

  if (!codes.length) return snapshots.length

  const testsToMaster = codes.map((code) => {
    const index = snapshots.findIndex(snapshot => isMasteredAt(snapshot, code))

    return index === -1 ? snapshots.length : index
  })

  return testsToMaster.reduce((sum, value) => sum + value, 0) / codes.length
}

const MIN_STRANDS_TO_COMPARE = 2

const pickExtremes = <T>(items: T[], score: (item: T) => number): { highest: T, lowest: T } | null => {
  const sorted = [...items].sort((a, b) => score(b) - score(a))
  const highest = sorted.at(0)
  const lowest = sorted.at(-1)

  if (!highest || !lowest || score(highest) === score(lowest)) return null

  return { highest, lowest }
}

export const getHighlights = (
  snapshots: MasteryTestSnapshot[],
  domains: MasteryMapDomain[],
): MasteryHighlights | null => {
  const latest = snapshots.at(-1)

  if (!latest) return null

  const strands = getStrands(latest, domains)

  if (strands.length < MIN_STRANDS_TO_COMPARE) return null

  const byPercentage = pickExtremes(strands, strand => strand.percentage)

  if (byPercentage) return { strongest: byPercentage.highest, focus: byPercentage.lowest }

  const hasOnlyFirstTest = snapshots.length === 1

  if (hasOnlyFirstTest) return null

  const byPace = pickExtremes(strands, strand => getMasteryPace(snapshots, strand.code))

  return byPace ? { strongest: byPace.lowest, focus: byPace.highest } : null
}
